import moment from 'moment';
import { ModelOverview, ShowroomModel } from '../typings/model-overview';
import { ImagesModelMap, ImageModel } from '../typings/images-model-map';
import isBrowser from 'is-browser';
import { AppConstants } from './app-constants';
import { ReviewModel, Review, Page } from '../typings/reviews';
import _ from 'lodash';

/**
 * Format modified date by 'dddd,MMM DD, YYYY'
 * @param dateString
 */
export const formatModifiedDate = (dateString: string) => {
  return moment(dateString).format('dddd, MMM DD, YYYY');
};

/**
 * get formated date by 'MM-DD-YYYY'
 * @param dateString
 */
export const getFormmatedDate = (dateString: string) => {
  return moment(dateString, 'MM-DD-YYYY').format('MM/DD/YYYY');
};

export const getSlug = (
  modelOverview: ModelOverview,
  showroomModel: ShowroomModel,
): string => {
  let result: string = '';
  modelOverview.models.forEach(function(model) {
    if (model.data.key?.carlineId == showroomModel.carlineId) {
      result = model.nodeId.replace('/', '');
    }
  });
  return result;
};

/**
 * Return white or black acording the hexcolor
 * @param hexcolor An hexadecimal color like #000, 000, 000000
 */
export const getHexContrast = (hexcolor: string) => {
  let excolorAux = hexcolor;

  // If a leading # is provided, remove it
  if (excolorAux.slice(0, 1) === '#') {
    excolorAux = excolorAux.slice(1);
  }

  // If a three-character hexcode, make six-character
  if (excolorAux.length === 3) {
    excolorAux = excolorAux
      .split('')
      .map(hex => hex + hex)
      .join('');
  }

  // Convert to RGB value
  const r = parseInt(excolorAux.substr(0, 2), 16);
  const g = parseInt(excolorAux.substr(2, 2), 16);
  const b = parseInt(excolorAux.substr(4, 2), 16);

  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 128 ? 'black' : 'white';
};

/**
 *
 * @param modelMap
 * @param slug
 */
export const getImageMapbySlug = (
  modelMap: ImagesModelMap,
  slug: string,
): ImageModel => {
  if (slug && modelMap[slug]) {
    return modelMap[slug];
  }
  return modelMap.default;
};

/**
 * Validate if the color name is a valid css color
 * @param stringToTest Color name or hex color
 */
export const isValidTextColor = (stringToTest: string) => {
  if (!isBrowser) {
    return false;
  }

  // Alter the following conditions according to your need.
  if (stringToTest === '') {
    return false;
  }
  if (stringToTest === 'inherit') {
    return false;
  }
  if (stringToTest === 'transparent') {
    return false;
  }

  const image = document.createElement('img');
  image.style.color = 'rgb(0, 0, 0)';
  image.style.color = stringToTest;
  if (image.style.color !== 'rgb(0, 0, 0)') {
    return true;
  }
  image.style.color = 'rgb(255, 255, 255)';
  image.style.color = stringToTest;

  return image.style.color !== 'rgb(255, 255, 255)';
};

/**
 * Given a color name this function return the hexadecimal value
 * @param str Color name
 */
export const getHexColorByName = (str: string): string => {
  if (!isBrowser) {
    return '#000';
  }

  const ctx = document.createElement('canvas').getContext('2d');
  if (ctx) {
    ctx.fillStyle = str;

    return ctx.fillStyle;
  }

  return '#000';
};

/**
 * Validate if is a valid hex color
 * @param hexColor Hex color format, you can use it without #
 */
export const isValidHexColor = (hexColor: string) => {
  const hexColorRegExp = /(^#?[a-f0-9]{6}$|^#?[a-f0-9]{3}$)/;

  return hexColorRegExp.test(hexColor);
};

/**
 * Return the contrast white or black using the given color
 * @param ele RBG, Color name, or hexadecimal color
 */
export const getContrastColorText = (ele: any) => {
  if (typeof ele === 'string' || ele instanceof String) {
    if (isValidHexColor(ele as string)) {
      return getHexContrast(ele as string);
    }

    if (isValidTextColor(ele as string)) {
      return getHexContrast(getHexColorByName(ele as string) as string);
    }

    return 'black';
  }

  return getRgbContrast(ele);
};

/**
 * Return white or black acording the RGB color
 * @param rgb RGB color with this format [225, 0, 0]
 * the fourth parameter can be passed but will be ignored
 */
export const getRgbContrast = (rgb: any) => {
  // randomly update
  rgb[0] = Math.round(Math.random() * 255);
  rgb[1] = Math.round(Math.random() * 255);
  rgb[2] = Math.round(Math.random() * 255);

  const o = Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000);

  return o > 125 ? 'black' : 'white';
};

/**
 * Recognizes if url has defined query params
 * Auxiliary Function
 * @param url
 * @returns boolean
 */
const hasQueryParams = (url: string) => {
  const queryParamsPattern = new RegExp(/\?.+=.*/g);
  return queryParamsPattern.test(url);
};

/**
 * Gets query params for E13 pov
 * @param width
 * @param height
 * @param isNewFormat
 * @returns string
 */
const getPovE13Params = (
  width: string,
  height: string,
  isNewFormat: boolean,
) => {
  return isNewFormat
    ? `&width=${Math.floor(+width * 1.2)}&height=${Math.floor(
        +height * 1.2,
      )}&cut=1&quality=90&crop=${Math.floor(+width * 0.07)},${Math.floor(
        +height * 0.47,
      )},${width},${height}`
    : `&h=4100&quality=90&w=7900&height=${height}&width=${width}&x=900&y=3000`; //accuracy
  // : `&h=4200&quality=90&w=7500&width=${width}&x=1250&y=3000`; // cropped
  // : `&h=4000&quality=90&w=8000&width=${width}&x=700&y=3000`; // No centered
  // : `&h=4200&quality=90&w=7500&width=300&x=1020&y=300`; // smaller;
};

/**
 * Gets query params for E06 pov format
 * @param width
 * @param height
 * @param isNewFormat
 * @returns
 */
const getPovE06Params = (
  width: string,
  height: string,
  isNewFormat: boolean,
) => {
  return isNewFormat
    ? `&width=${Math.floor(+width * 1.2)}&height=${Math.floor(
        +height * 1.2,
      )}&cut=1&quality=90&crop=${Math.floor(+width * 0.06)},${Math.floor(
        +height * 0.47,
      )},${width},${height}`
    : `&h=4200&quality=90&w=7000&width=${width}&x=1050&y=3000`;
};

/**
 * Give properties to the images from iris.
 * The width and height need to be proportional to 960*432,
 * for instance, 480*216 and so on
 * @param mediaImageUrl Image from iris
 * @param pov Image position
 * @param width Image width
 * @param height Image height
 * @param modelYear Vehicle's year
 * @param modelName Vehicle's model
 * @param isNewImage conditional to not apply changes on image
 */
export const transformIrisUrl = (
  mediaImageUrl: string,
  pov: string,
  width = '960',
  height = '432',
  modelYear = '',
  modelName = '',
  isNewImage = false,
): string => {
  if (!mediaImageUrl) return '';

  // New Vicci images, encoded images...
  const urlQueryParams = new URLSearchParams(mediaImageUrl);
  const isIrisServer = mediaImageUrl.toLowerCase()?.includes('iris');
  if (isNewImage || !urlQueryParams.has('pov')) {
    const newWidthParam =
      hasQueryParams(mediaImageUrl) || isIrisServer
        ? `&width=${width}`
        : `?width=${width}`;
    return mediaImageUrl + newWidthParam;
  }

  // No NGW6 Images
  const useNewFormat =
    modelYear &&
    modelName &&
    +modelYear >= 2020 &&
    modelName.toLocaleLowerCase() !== 'passat'
      ? true
      : false;

  const isPassat2021 =
    modelYear &&
    modelName &&
    +modelYear == 2021 &&
    modelName.toLocaleLowerCase() === 'passat'
      ? true
      : false;

  const regex = /pov=(.*?),/g;
  const baseUrl = mediaImageUrl.replace(regex, `pov=${pov},`);
  switch (pov) {
    case AppConstants.IrisImgPositionE13:
      return (
        baseUrl + getPovE13Params(width, height, useNewFormat || isPassat2021)
      );
    case AppConstants.IrisImgPositionE06:
      return (
        baseUrl + getPovE06Params(width, height, useNewFormat || isPassat2021)
      );
    default:
      return (
        baseUrl + getPovE13Params(width, height, useNewFormat || isPassat2021)
      );
  }
};

export const getRatingPercent = (rating: number) => (rating / 5) * 100;

/**
 * Apply the scroll to top
 * @param scrollStepInPx Interval of px to scroll to top
 * @param behavior window.scrollTo parameter
 */
type Behavior = 'smooth' | 'auto' | undefined;
export const scrollToTop = (scrollStepInPx: number, behavior?: Behavior) => {
  if (behavior) {
    window.scrollTo({ top: 0, left: 0, behavior });
  } else {
    const intervalId = setInterval(() => {
      window.scroll(0, window.pageYOffset - scrollStepInPx);
      if (window.pageYOffset === 0) {
        clearInterval(intervalId);
      }
    }, 50);
  }
};
export type reviewsPages = { [key: number]: ReviewModel };

export const sortReviews = (reviews?: Review[]) => {
  return reviews?.reverse();
};
export const getPages = (totalElements: number, chunkSize?: number): Page => {
  const totalPages = Math.ceil((totalElements || 0) / (chunkSize || 0));
  return {
    size: chunkSize || 10,
    number: AppConstants.API_PAGINATION_BASE as number,
    totalPages: totalPages,
    totalElements: totalElements,
  };
};

/**
 * Enabled or disabled the scroll functionality
 * @param disabled Scroll state
 */
export const disabledScroll = (disabled: boolean) => {
  if (isBrowser) {
    disabled
      ? (document.body.style.overflow = 'hidden')
      : (document.body.style.overflow = 'inherit');
  }
};

/**
 * Unsets z-index value for pagemain container
 */
export const unsetPagemainZindex = () => {
  // The class name is used due to lack of id
  let pagemainContainer = document.getElementsByClassName('pagemain')[0];
  if (pagemainContainer && pagemainContainer.parentElement)
    pagemainContainer.parentElement.style.zIndex = 'unset';
};


/**
 * Give format to the Phone number
 * @param phone Phone number
 */
export const stringToPhoneFormat = (phone: string) =>
  phone?.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
